import {
  Banner,
  Body,
  FieldErrors,
  FieldValues,
  Label,
  Stack,
} from '@qred/components-library';
import React from 'react';
import { logToSentry } from '~/helpers/loggers.helper';
import useTranslate from '~/hooks/useTranslate';

interface Props<T extends FieldValues> {
  errors: FieldErrors<T>;
}

export const FormErrorBanner = <T extends FieldValues>(props: Props<T>) => {
  const { errors } = props;

  const t = useTranslate();

  const errorList = extractErrors(errors);

  return (
    <Banner
      opened={errorList.length > 0}
      message={
        <Stack spacing={'xs'}>
          <Label size="lg">{t('FormErrorBannerTitle')}</Label>
          <Body size="lg">
            <ul style={{ listStyleType: 'unset', marginLeft: '2em' }}>
              {errorList.map(({ path, message }) => {
                const fieldName = getFieldName(path);
                return (
                  <li key={path}>{`${
                    fieldName ? (t(fieldName) as string) : path
                  }: ${t(message) as string}`}</li>
                );
              })}
            </ul>
          </Body>
        </Stack>
      }
      state={'critical'}
      withCloseButton={false}
    />
  );
};

const extractErrors = <T extends FieldValues>(
  fieldErrors: FieldErrors<T>,
  parentPath = ''
): { path: string; message: string }[] => {
  const errorList: { path: string; message: string }[] = [];

  Object.entries(fieldErrors).forEach(([key, value]) => {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    if (value && typeof value === 'object') {
      if (value.message && typeof value.message === 'string') {
        errorList.push({
          path: currentPath,
          message: value.message,
        });
      } else if (!value.message) {
        errorList.push(...extractErrors(value as FieldErrors<T>, currentPath));
      }
    }
  });

  return errorList;
};

const getFieldName = (path: string) => {
  switch (path) {
    case 'turnover':
      return 'Onboarding.TurnoverLabel';
    case 'loanPurpose.purposeOfLoan':
      return 'LoanPurpose';
    case 'companyDetails':
    case 'clientId':
    case 'companySearch':
    case 'companySearchTerm':
    case 'selectedCompany':
      return 'Onboarding.SelectCompanySearchInputLabel';
    case 'loanPurpose.reason':
      return 'Onboarding.LoanPurposeSpecifyPurpose';
    case 'companyName':
      return 'Onboarding.SelectCompanySearchByCompanyNameLabel';
    case 'companyAddress':
      return 'Onboarding.SelectCompanySearchByCompanyAddressLabel';
    case 'firstName':
      return 'FirstName';
    case 'lastName':
      return 'LastName';
    case 'dateOfBirth':
      return 'DateOfBirth';
    case 'mobilePhone':
      return 'MobilePhone';
    case 'phone':
      return 'MobilePhone';
    case 'email':
      return 'Email';
    case 'addressFieldValue':
    case 'selectedAddress':
      return 'Onboarding.ApplicantAddress';
    case 'personalNumber':
      return 'PersonalNumber';
    case 'address.zipCode':
      return 'AddressFields.PostCode';
    case 'address.houseNumber':
      return 'AddressFields.HouseNumber';
    case 'address.houseNumberAddition':
      return 'AddressFields.HouseNumberAddition';
    case 'address':
    case 'root.address':
      return 'Onboarding.ApplicantAddress';
    default:
      logToSentry(
        new Error(`FormErrorBanner missing field name translation (${path})`),
        'FormErrorBanner getFieldName'
      );
      return null;
  }
};
